import { useState, useCallback } from "react";
import { convertSingle, convertBatch } from "../api/client";
import type {
  ConversionConfig,
  ConvertResponse,
  BatchConvertResponse,
  AppStatus,
} from "../types/sigwaz";

export function useConverter(config: ConversionConfig) {
  const [status, setStatus] = useState<AppStatus>({ kind: "idle" });
  const [singleResult, setSingleResult] = useState<ConvertResponse | null>(null);
  const [batchResult, setBatchResult] = useState<BatchConvertResponse | null>(null);

  const runSingle = useCallback(
    async (yaml: string) => {
      if (!yaml.trim()) return;
      setStatus({ kind: "converting" });
      setSingleResult(null);
      setBatchResult(null);
      try {
        const res = await convertSingle(yaml, config);
        setSingleResult(res);
        setStatus(
          res.result.errors.length > 0
            ? { kind: "error", message: res.result.errors[0] }
            : { kind: "success", message: `Rule ${res.result.rule_ids[0] ?? "-"} generated` }
        );
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        setStatus({ kind: "error", message: msg });
      }
    },
    [config]
  );

  const runBatch = useCallback(
    async (yaml: string) => {
      if (!yaml.trim()) return;
      setStatus({ kind: "converting" });
      setBatchResult(null);
      setSingleResult(null);
      try {
        const res = await convertBatch(yaml, config);
        setBatchResult(res);
        setStatus(
          res.total_errors > 0
            ? { kind: "error", message: `${res.total_errors} error(s)` }
            : { kind: "success", message: `${res.total_rules} Wazuh rules generated` }
        );
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        setStatus({ kind: "error", message: msg });
      }
    },
    [config]
  );

  const reset = useCallback(() => {
    setStatus({ kind: "idle" });
    setSingleResult(null);
    setBatchResult(null);
  }, []);

  return { status, singleResult, batchResult, runSingle, runBatch, reset };
}
